/**
 * LegalDisclaimerModal — Risk acknowledgement gate
 * Triggered by CTA clicks on the landing page (not auto-popup).
 * After acceptance, sets both sessionStorage + localStorage keys so the
 * in-app YieldExplorer duplicate never fires a second time.
 */
import React, { useState, useEffect } from 'react';
import { AlertTriangle, Shield, TrendingDown, Lock, ExternalLink, CheckCircle2 } from 'lucide-react';

export default function LegalDisclaimerModal({ forceOpen = false, onAccepted }) {
  const [visible, setVisible] = useState(false);

  // Only show when explicitly triggered via forceOpen
  useEffect(() => {
    if (forceOpen) setVisible(true);
  }, [forceOpen]);

  const accept = () => {
    // Satisfy in-app risk acknowledgement so YieldExplorer doesn't show its own
    sessionStorage.setItem('profitspot_disclaimer_v1', 'true');
    localStorage.setItem('profitspot_risk_acknowledged', 'true');
    setVisible(false);
    onAccepted && onAccepted();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.90)', backdropFilter: 'blur(8px)' }}
      data-testid="legal-disclaimer-modal"
    >
      <div
        className="bg-[#0d0d1a] border border-gray-700/60 rounded-2xl w-full max-w-lg shadow-2xl flex flex-col"
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#ffa502]/10 border border-[#ffa502]/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-[#ffa502]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Risk Disclosure</h2>
              <p className="text-xs text-gray-500">Please read before using ProfitSpot</p>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4 text-sm text-gray-300">
          {/* Critical notice */}
          <div className="bg-red-500/10 border border-red-500/25 rounded-xl p-4">
            <p className="font-semibold text-red-400 mb-1 text-sm">Important Notice</p>
            <p className="text-xs text-gray-300 leading-relaxed">
              <strong className="text-white">Cryptocurrency and DeFi investments carry substantial risk of loss.</strong>{' '}
              ProfitSpot provides informational data only — this is{' '}
              <strong className="text-white">not financial, investment, legal, or tax advice.</strong>{' '}
              You could lose some or all of your invested capital.
            </p>
          </div>

          {/* Risk points */}
          <div className="space-y-3">
            <RiskPoint icon={Lock} color="#ef4444" title="Smart Contract & Protocol Risks">
              DeFi protocols may contain bugs, vulnerabilities, or be subject to exploits, rug pulls,
              and governance attacks — even audited protocols have been hacked. Your funds are at risk
              when depositing to any protocol.
            </RiskPoint>

            <RiskPoint icon={TrendingDown} color="#ffa502" title="Market & Impermanent Loss">
              Providing liquidity exposes you to impermanent loss (IL) when token prices diverge.
              High advertised APY does not guarantee profitability. Token values can drop significantly,
              and gas fees may exceed your yield on smaller positions.
            </RiskPoint>

            <RiskPoint icon={Shield} color="#00d4ff" title="Data Accuracy">
              ProfitSpot aggregates data from third-party sources (DeFiLlama, GoPlus, public RPCs).
              Data may be delayed, incomplete, or inaccurate. APY figures are estimates based on
              current conditions — historical performance does not predict future results.
            </RiskPoint>
          </div>

          {/* Acknowledgement */}
          <div className="bg-[#00ff88]/5 border border-[#00ff88]/20 rounded-xl p-4">
            <p className="text-xs text-gray-300 leading-relaxed">
              By clicking{' '}
              <strong className="text-[#00ff88]">"I Understand &amp; Agree"</strong>{' '}
              you confirm that you have read this disclosure, are aware of DeFi investment risks,
              and that all investment decisions are your own responsibility. ProfitSpot is not
              liable for any investment losses.
            </p>
          </div>

          {/* Full disclaimer link */}
          <div className="text-center pb-1">
            <a
              href="/disclaimer"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Read full Risk Disclaimer
            </a>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="px-6 py-5 border-t border-gray-800 flex-shrink-0 space-y-3">
          <button
            onClick={accept}
            className="w-full flex items-center justify-center gap-2 py-3 bg-[#00ff88] text-black font-bold rounded-xl text-sm hover:bg-[#00cc6a] transition-colors"
            data-testid="disclaimer-agree-btn"
          >
            <CheckCircle2 className="w-4 h-4" />
            I Understand &amp; Agree — Continue to ProfitSpot
          </button>
          <p className="text-center text-[10px] text-gray-600">
            You must acknowledge this disclosure to use the platform.
          </p>
        </div>
      </div>
    </div>
  );
}

function RiskPoint({ icon: Icon, color, title, children }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-[#0a0a12] rounded-lg border border-gray-800/60">
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color }} />
      <div>
        <p className="text-xs font-semibold text-white mb-0.5">{title}</p>
        <p className="text-[11px] text-gray-400 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}
